import * as THREE from "three";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { randomRange } from "../../../../common/common_funcs.js";
import { IEmitter3DConfig } from "../../../core/emitters/emitter.js";
import { PayloadEmitter } from "../../../core/emitters/payload_emitter.js";
import { IVfxTask, VfxTaskRunner } from "../../../core/runtime/vfx_task_runner.js";
import { IController, StopBehavior } from "../../../core/types/controller_types.js";
import { Vfx3DRuntime } from "../../../vfx_3d_runtime.js";
import { VfxGradientAtlas } from "../../../core/textures/vfx_gradient_atlas.js";
import {
  VfxFireworkTrailBatchBase,
  VfxFireworkTrailAdditiveBatch,
  VfxFireworkTrailNormalBatch,
} from "./vfx_firework_trail_base_batch.js";

export interface IRocketTrailPayload
{
  res_id: ResourceID;
  gradient_id: number;
  size: THREE.Vector2;
  lifespan: number;
  deviation: number;
  min_speed: number;
  max_speed: number;
  batch_type: typeof VfxFireworkTrailAdditiveBatch | typeof VfxFireworkTrailNormalBatch;
  fade_in_end: number;
  fade_out_start: number;
  max_alpha: number;
}

export class RocketTrailParticleEmitter extends PayloadEmitter<IRocketTrailPayload>
{
  private static readonly scratch_fly_dir: THREE.Vector3  = new THREE.Vector3();
  private static readonly scratch_temp_vec: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_right: THREE.Vector3    = new THREE.Vector3();
  private static readonly scratch_up: THREE.Vector3       = new THREE.Vector3();

  private readonly batch: VfxFireworkTrailBatchBase;

  public constructor( position: THREE.Vector3, payload: IRocketTrailPayload, config?: IEmitter3DConfig )
  {
    super( position, payload, config );
    this.batch = Vfx3DRuntime.getBatch( this.payload.batch_type as any ) as VfxFireworkTrailBatchBase;
  }

  protected override emitParticle( spawn_pos: THREE.Vector3, move_dir: THREE.Vector3, now: number ): void
  {
    const p = this.payload;

    const fly_dir = RocketTrailParticleEmitter.scratch_fly_dir.copy( move_dir ).negate().normalize();
    if ( fly_dir.lengthSq() < 0.0001 )
      fly_dir.set( 0, 0, 1 );

    const is_near_vertical = Math.abs( fly_dir.y ) >= 0.99;
    const temp_vec = RocketTrailParticleEmitter.scratch_temp_vec.set(
      is_near_vertical ? 1 : 0,
      is_near_vertical ? 0 : 1,
      0
    );
    const right = RocketTrailParticleEmitter.scratch_right.crossVectors( fly_dir, temp_vec ).normalize();
    const up    = RocketTrailParticleEmitter.scratch_up.crossVectors( right, fly_dir ).normalize();

    const spread_angle     = randomRange( 0, Math.PI * 2 );
    const spread_intensity = randomRange( 0, p.deviation );
    fly_dir.addScaledVector( right, Math.cos( spread_angle ) * spread_intensity );
    fly_dir.addScaledVector( up, Math.sin( spread_angle ) * spread_intensity );
    fly_dir.normalize();

    const speed    = randomRange( p.min_speed, p.max_speed );
    const velocity = fly_dir.multiplyScalar( speed );

    this.batch.spawnParticle(
      p.res_id,
      p.gradient_id,
      spawn_pos,
      velocity,
      p.size,
      now,
      p.lifespan,
      p.fade_in_end,
      p.fade_out_start,
      p.max_alpha
    );
  }
}

export class VfxRocketTrailTask implements IVfxTask, IController
{
  private static glow_gradient_id: number  = -1;
  private static smoke_gradient_id: number = -1;

  public is_active: boolean = true;
  private readonly sparks_emitter: RocketTrailParticleEmitter;
  private readonly smoke_emitter: RocketTrailParticleEmitter;

  // --- Layer 1 Constants (Sparks / Glow) ---
  private static readonly RATE_OVER_DISTANCE_L1: number = 12;
  private static readonly DEVIATION_L1: number          = 0.2;
  private static readonly MIN_SPEED_L1: number          = 2;
  private static readonly MAX_SPEED_L1: number          = 4;
  private static readonly LIFESPAN_L1: number           = 0.3;
  private static readonly SIZE_L1: number               = 0.6;
  private static readonly SIZE_VEC_L1: THREE.Vector2    = new THREE.Vector2( VfxRocketTrailTask.SIZE_L1, VfxRocketTrailTask.SIZE_L1 );

  private static readonly COLOR_1_L1: THREE.Color       = new THREE.Color( "#FFFF50" );
  private static readonly COLOR_2_L1: THREE.Color       = new THREE.Color( "#FF7814" );
  private static readonly COLOR_3_L1: THREE.Color       = new THREE.Color( "#FF5014" );
  private static readonly COLOR_STAGE_1_L1: number      = 0.2;
  private static readonly COLOR_STAGE_2_L1: number      = 0.5;
  private static readonly FADE_IN_END_L1: number        = 0.2;
  private static readonly FADE_OUT_START_L1: number     = 0.7;
  private static readonly MAX_ALPHA_L1: number          = 1.0;

  // --- Layer 2 Constants (Smoke) ---
  private static readonly RATE_OVER_DISTANCE_L2: number = 20;
  private static readonly DEVIATION_L2: number          = 0.7;
  private static readonly MIN_SPEED_L2: number          = 2;
  private static readonly MAX_SPEED_L2: number          = 3;
  private static readonly LIFESPAN_L2: number           = 0.4;
  private static readonly SIZE_L2: number               = 1;
  private static readonly SIZE_VEC_L2: THREE.Vector2    = new THREE.Vector2( VfxRocketTrailTask.SIZE_L2, VfxRocketTrailTask.SIZE_L2 );

  private static readonly COLOR_SMOKE: THREE.Color      = new THREE.Color( "#FFFFFF" );
  private static readonly FADE_IN_END_L2: number        = 0.5;
  private static readonly FADE_OUT_START_L2: number     = 0.7;
  private static readonly MAX_ALPHA_L2: number          = 0.25;

  public constructor( position: THREE.Vector3 )
  {
    if ( VfxRocketTrailTask.glow_gradient_id === -1 )
    {
      VfxRocketTrailTask.glow_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxRocketTrailTask.COLOR_1_L1 },
        { stop: VfxRocketTrailTask.COLOR_STAGE_1_L1, color: VfxRocketTrailTask.COLOR_2_L1 },
        { stop: VfxRocketTrailTask.COLOR_STAGE_2_L1, color: VfxRocketTrailTask.COLOR_3_L1 },
        { stop: 1.0, color: VfxRocketTrailTask.COLOR_3_L1 },
      ]);
    }

    if ( VfxRocketTrailTask.smoke_gradient_id === -1 )
    {
      VfxRocketTrailTask.smoke_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxRocketTrailTask.COLOR_SMOKE },
        { stop: 1.0, color: VfxRocketTrailTask.COLOR_SMOKE },
      ]);
    }

    this.sparks_emitter = new RocketTrailParticleEmitter( position, {
      res_id: ResourceID.CircleGlow1,
      gradient_id: VfxRocketTrailTask.glow_gradient_id,
      size: VfxRocketTrailTask.SIZE_VEC_L1,
      lifespan: VfxRocketTrailTask.LIFESPAN_L1,
      deviation: VfxRocketTrailTask.DEVIATION_L1,
      min_speed: VfxRocketTrailTask.MIN_SPEED_L1,
      max_speed: VfxRocketTrailTask.MAX_SPEED_L1,
      batch_type: VfxFireworkTrailAdditiveBatch,
      fade_in_end: VfxRocketTrailTask.FADE_IN_END_L1,
      fade_out_start: VfxRocketTrailTask.FADE_OUT_START_L1,
      max_alpha: VfxRocketTrailTask.MAX_ALPHA_L1,
    }, { rate_over_distance: VfxRocketTrailTask.RATE_OVER_DISTANCE_L1 } );

    this.smoke_emitter = new RocketTrailParticleEmitter( position, {
      res_id: ResourceID.CircleGlow2,
      gradient_id: VfxRocketTrailTask.smoke_gradient_id,
      size: VfxRocketTrailTask.SIZE_VEC_L2,
      lifespan: VfxRocketTrailTask.LIFESPAN_L2,
      deviation: VfxRocketTrailTask.DEVIATION_L2,
      min_speed: VfxRocketTrailTask.MIN_SPEED_L2,
      max_speed: VfxRocketTrailTask.MAX_SPEED_L2,
      batch_type: VfxFireworkTrailNormalBatch,
      fade_in_end: VfxRocketTrailTask.FADE_IN_END_L2,
      fade_out_start: VfxRocketTrailTask.FADE_OUT_START_L2,
      max_alpha: VfxRocketTrailTask.MAX_ALPHA_L2,
    }, { rate_over_distance: VfxRocketTrailTask.RATE_OVER_DISTANCE_L2 } );
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.sparks_emitter.setPosition( pos );
    this.smoke_emitter.setPosition( pos );
  }

  public stop( behavior?: StopBehavior ): void
  {
    this.is_active = false;
    this.sparks_emitter.stop( behavior );
    this.smoke_emitter.stop( behavior );
  }

  public start(): void
  {
    this.is_active = true;
    this.sparks_emitter.start();
    this.smoke_emitter.start();
  }

  public accelerate( factor: number ): void
  {
    this.sparks_emitter.accelerate( factor );
    this.smoke_emitter.accelerate( factor );
  }

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    const sparks_done = this.sparks_emitter.onUpdate( now );
    const smoke_done  = this.smoke_emitter.onUpdate( now );

    return sparks_done && smoke_done;
  }
}

export class VfxRocketTrail
{
  public spawn( position: THREE.Vector3 ): IController
  {
    const task = new VfxRocketTrailTask( position );
    VfxTaskRunner.addTask( task );
    return task;
  }
}
